import { Tooltip } from 'antd';
import type {
  MouseEvent as ReactMouseEvent,
  PointerEvent as ReactPointerEvent,
  SyntheticEvent,
} from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const DEFAULT_FRAME_COUNT = 18;
const TIMELINE_FRAME_HEIGHT = 80;
const MIN_RANGE_RATIO = 0.04;
const RANGE_HANDLE_WIDTH = 24;
const DEFAULT_VIDEO_ASPECT_RATIO = 16 / 9;
const PLAYBACK_END_TOLERANCE = 0.03;
const PLAYBACK_RESTART_TOLERANCE = 0.15;

type TimelineFrame = {
  src: string;
  time: number;
};

type PlayRange = {
  start: number;
  end: number;
};

type RangeDragState = {
  type: 'start' | 'end' | 'window';
  startClientX: number;
  initialRange: PlayRange;
};

export type VideoRange = [number, number];

export type VideoRangeSelectorProps = {
  videoSrc: string;
  value?: VideoRange | null;
  onChange: (value: VideoRange) => void;
};

function formatTime(time: number) {
  if (!Number.isFinite(time)) {
    return '00:00.000';
  }

  const totalMilliseconds = Math.max(0, Math.round(time * 1000));
  const minutes = Math.floor(totalMilliseconds / 60000);
  const seconds = Math.floor((totalMilliseconds % 60000) / 1000);
  const milliseconds = totalMilliseconds % 1000;

  return `${minutes.toString().padStart(2, '0')}:${seconds
    .toString()
    .padStart(2, '0')}.${milliseconds.toString().padStart(3, '0')}`;
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function playRangeToSeconds(range: PlayRange, duration: number): VideoRange {
  return [range.start * duration, range.end * duration];
}

function secondsToPlayRange(range: VideoRange, duration: number): PlayRange {
  if (!duration) {
    return {
      start: 0,
      end: 1,
    };
  }

  const start = clamp(range[0], 0, duration);
  const end = clamp(range[1], start, duration);

  return {
    start: start / duration,
    end: end / duration,
  };
}

function waitForEvent(target: HTMLVideoElement, eventName: string) {
  return new Promise<void>((resolve, reject) => {
    const onReady = () => {
      cleanup();
      resolve();
    };
    const onError = () => {
      cleanup();
      reject(new Error(`Video ${eventName} failed`));
    };
    const cleanup = () => {
      target.removeEventListener(eventName, onReady);
      target.removeEventListener('error', onError);
    };

    target.addEventListener(eventName, onReady, { once: true });
    target.addEventListener('error', onError, { once: true });
  });
}

function getVideoAspectRatio(video: HTMLVideoElement) {
  if (!video.videoWidth || !video.videoHeight) {
    return DEFAULT_VIDEO_ASPECT_RATIO;
  }

  return video.videoWidth / video.videoHeight;
}

function drawFrame(video: HTMLVideoElement) {
  const frameWidth = Math.max(
    1,
    Math.round(TIMELINE_FRAME_HEIGHT * getVideoAspectRatio(video)),
  );
  const canvas = document.createElement('canvas');
  canvas.width = frameWidth;
  canvas.height = TIMELINE_FRAME_HEIGHT;

  const context = canvas.getContext('2d');

  if (!context || !video.videoWidth || !video.videoHeight) {
    return '';
  }

  context.drawImage(
    video,
    0,
    0,
    video.videoWidth,
    video.videoHeight,
    0,
    0,
    frameWidth,
    TIMELINE_FRAME_HEIGHT,
  );

  return canvas.toDataURL('image/jpeg', 0.72);
}

async function createTimelineFrames(src: string, frameCount: number) {
  const frameVideo = document.createElement('video');
  frameVideo.crossOrigin = 'anonymous';
  frameVideo.muted = true;
  frameVideo.playsInline = true;
  frameVideo.preload = 'auto';
  frameVideo.src = src;

  await waitForEvent(frameVideo, 'loadedmetadata');

  if (!Number.isFinite(frameVideo.duration) || frameVideo.duration <= 0) {
    return [];
  }

  const frames: TimelineFrame[] = [];
  const lastSafeFrameTime = Math.max(frameVideo.duration - 0.08, 0);

  for (let index = 0; index < frameCount; index += 1) {
    const time = Math.min(
      ((index + 0.5) / frameCount) * frameVideo.duration,
      lastSafeFrameTime,
    );

    frameVideo.currentTime = time;
    await waitForEvent(frameVideo, 'seeked');

    const frameSrc = drawFrame(frameVideo);

    if (frameSrc) {
      frames.push({ src: frameSrc, time });
    }
  }

  frameVideo.removeAttribute('src');
  frameVideo.load();

  return frames;
}

export function VideoRangeSelector({
  videoSrc,
  value,
  onChange,
}: VideoRangeSelectorProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const timelineRef = useRef<HTMLDivElement | null>(null);
  const rangeDragMovedRef = useRef(false);
  const defaultRangeEmittedRef = useRef<string | null>(null);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [rangeDrag, setRangeDrag] = useState<RangeDragState | null>(null);
  const [timelineWidth, setTimelineWidth] = useState(0);
  const [timelineHeight, setTimelineHeight] = useState(0);
  const [videoAspectRatio, setVideoAspectRatio] = useState(
    DEFAULT_VIDEO_ASPECT_RATIO,
  );
  const [playRange, setPlayRange] = useState<PlayRange>({
    start: 0,
    end: 1,
  });
  const [frames, setFrames] = useState<TimelineFrame[]>([]);
  const [isGeneratingFrames, setIsGeneratingFrames] = useState(true);

  const rangeStartTime = playRange.start * duration;
  const rangeEndTime = playRange.end * duration;
  const rangeStartPx = playRange.start * timelineWidth;
  const rangeEndPx = playRange.end * timelineWidth;
  const timelineFrameHeight = timelineHeight || TIMELINE_FRAME_HEIGHT;
  const timelineFrameWidth = Math.max(
    1,
    Math.round(timelineFrameHeight * videoAspectRatio),
  );
  const timelineFrameCount =
    timelineWidth > 0
      ? Math.max(1, Math.ceil(timelineWidth / timelineFrameWidth) + 1)
      : DEFAULT_FRAME_COUNT;

  const progress = useMemo(() => {
    if (!duration) {
      return 0;
    }

    return Math.min(Math.max(currentTime / duration, 0), 1);
  }, [currentTime, duration]);

  const selectedProgress = useMemo(() => {
    const rangeWidth = playRange.end - playRange.start;

    if (!rangeWidth) {
      return 0;
    }

    return clamp((progress - playRange.start) / rangeWidth, 0, 1);
  }, [playRange.end, playRange.start, progress]);

  const clientXToRatio = useCallback((clientX: number) => {
    const timeline = timelineRef.current;

    if (!timeline) {
      return 0;
    }

    const rect = timeline.getBoundingClientRect();

    return clamp((clientX - rect.left) / rect.width, 0, 1);
  }, []);

  const seekToClientX = useCallback(
    (clientX: number) => {
      const video = videoRef.current;

      if (!video || !duration) {
        return;
      }

      const ratio = clamp(
        clientXToRatio(clientX),
        playRange.start,
        playRange.end,
      );
      const nextTime = ratio * duration;

      video.currentTime = nextTime;
      setCurrentTime(nextTime);
    },
    [clientXToRatio, duration, playRange.end, playRange.start],
  );

  const seekToRatio = useCallback(
    (ratio: number) => {
      const video = videoRef.current;

      if (!video || !duration) {
        return;
      }

      const nextTime = clamp(ratio, 0, 1) * duration;

      video.currentTime = nextTime;
      setCurrentTime(nextTime);
    },
    [duration],
  );

  const handlePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      event.currentTarget.setPointerCapture(event.pointerId);
      setIsDragging(true);
    },
    [],
  );

  const handleMouseDown = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragging(true);
    },
    [],
  );

  const handlePointerMove = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (!isDragging) {
        return;
      }

      event.preventDefault();
      seekToClientX(event.clientX);
    },
    [isDragging, seekToClientX],
  );

  const stopDragging = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      event.stopPropagation();

      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }

      setIsDragging(false);
    },
    [],
  );

  const startRangeDrag = useCallback(
    (
      type: RangeDragState['type'],
      event: ReactPointerEvent<HTMLDivElement>,
    ) => {
      event.stopPropagation();
      event.currentTarget.setPointerCapture(event.pointerId);
      rangeDragMovedRef.current = false;
      setRangeDrag({
        type,
        startClientX: event.clientX,
        initialRange: playRange,
      });
    },
    [playRange],
  );

  const stopRangeDrag = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }

      setRangeDrag(null);
    },
    [],
  );

  useEffect(() => {
    defaultRangeEmittedRef.current = null;
    setCurrentTime(0);
    setDuration(0);
    setFrames([]);
    setIsGeneratingFrames(true);
    setPlayRange({ start: 0, end: 1 });
  }, [videoSrc]);

  useEffect(() => {
    if (!duration || !value) {
      return;
    }

    setPlayRange(secondsToPlayRange(value, duration));
  }, [duration, value]);

  useEffect(() => {
    if (!duration) {
      return;
    }

    const defaultRangeKey = `${videoSrc}:${duration}`;

    if (defaultRangeEmittedRef.current === defaultRangeKey) {
      return;
    }

    defaultRangeEmittedRef.current = defaultRangeKey;
    setPlayRange({ start: 0, end: 1 });
    onChange([0, Math.min(5, duration)]);
  }, [duration, onChange, videoSrc]);

  useEffect(() => {
    const timeline = timelineRef.current;

    if (!timeline) {
      return;
    }

    const updateTimelineSize = () => {
      const rect = timeline.getBoundingClientRect();
      setTimelineWidth(rect.width);
      setTimelineHeight(rect.height);
    };

    updateTimelineSize();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateTimelineSize);

      return () => {
        window.removeEventListener('resize', updateTimelineSize);
      };
    }

    const resizeObserver = new ResizeObserver(updateTimelineSize);
    resizeObserver.observe(timeline);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!isDragging) {
      return;
    }

    const handleWindowMove = (event: PointerEvent | MouseEvent) => {
      seekToClientX(event.clientX);
    };
    const handleWindowUp = () => {
      setIsDragging(false);
    };

    window.addEventListener('pointermove', handleWindowMove);
    window.addEventListener('pointerup', handleWindowUp);
    window.addEventListener('mousemove', handleWindowMove);
    window.addEventListener('mouseup', handleWindowUp);

    return () => {
      window.removeEventListener('pointermove', handleWindowMove);
      window.removeEventListener('pointerup', handleWindowUp);
      window.removeEventListener('mousemove', handleWindowMove);
      window.removeEventListener('mouseup', handleWindowUp);
    };
  }, [isDragging, seekToClientX]);

  useEffect(() => {
    if (!rangeDrag) {
      return;
    }

    const updateRangeFromClientX = (clientX: number) => {
      const timeline = timelineRef.current;

      if (!timeline || !duration) {
        return;
      }

      const rect = timeline.getBoundingClientRect();
      const deltaRatio = (clientX - rangeDrag.startClientX) / rect.width;
      const { initialRange } = rangeDrag;
      let nextRange: PlayRange;

      if (Math.abs(clientX - rangeDrag.startClientX) > 3) {
        rangeDragMovedRef.current = true;
      }

      if (rangeDrag.type === 'start') {
        nextRange = {
          start: clamp(
            initialRange.start + deltaRatio,
            0,
            initialRange.end - MIN_RANGE_RATIO,
          ),
          end: initialRange.end,
        };
      } else if (rangeDrag.type === 'end') {
        nextRange = {
          start: initialRange.start,
          end: clamp(
            initialRange.end + deltaRatio,
            initialRange.start + MIN_RANGE_RATIO,
            1,
          ),
        };
      } else {
        const width = initialRange.end - initialRange.start;
        const nextStart = clamp(initialRange.start + deltaRatio, 0, 1 - width);

        nextRange = {
          start: nextStart,
          end: nextStart + width,
        };
      }

      setPlayRange(nextRange);
      onChange(playRangeToSeconds(nextRange, duration));

      if (rangeDrag.type === 'start') {
        seekToRatio(nextRange.start);
      } else if (rangeDrag.type === 'end') {
        seekToRatio(nextRange.end);
      } else if (currentTime < nextRange.start * duration) {
        seekToRatio(nextRange.start);
      } else if (currentTime > nextRange.end * duration) {
        seekToRatio(nextRange.end);
      }
    };

    const handleWindowMove = (event: PointerEvent | MouseEvent) => {
      updateRangeFromClientX(event.clientX);
    };
    const handleWindowUp = () => {
      setRangeDrag(null);
    };

    window.addEventListener('pointermove', handleWindowMove);
    window.addEventListener('pointerup', handleWindowUp);
    window.addEventListener('mousemove', handleWindowMove);
    window.addEventListener('mouseup', handleWindowUp);

    return () => {
      window.removeEventListener('pointermove', handleWindowMove);
      window.removeEventListener('pointerup', handleWindowUp);
      window.removeEventListener('mousemove', handleWindowMove);
      window.removeEventListener('mouseup', handleWindowUp);
    };
  }, [currentTime, duration, onChange, rangeDrag, seekToRatio]);

  useEffect(() => {
    const video = videoRef.current;

    if (!video || !duration) {
      return;
    }

    if (currentTime < rangeStartTime || currentTime > rangeEndTime) {
      video.currentTime = rangeStartTime;
      setCurrentTime(rangeStartTime);
    }
  }, [currentTime, duration, rangeEndTime, rangeStartTime]);

  const togglePlayback = useCallback(async () => {
    const video = videoRef.current;

    if (!video) {
      return;
    }

    if (video.paused) {
      if (
        video.currentTime < rangeStartTime ||
        video.currentTime >= rangeEndTime - PLAYBACK_RESTART_TOLERANCE
      ) {
        video.currentTime = rangeStartTime;
        setCurrentTime(rangeStartTime);
      }

      try {
        await video.play();
        setIsPlaying(true);
      } catch {
        setIsPlaying(false);
      }
    } else {
      video.pause();
      setIsPlaying(false);
    }
  }, [rangeEndTime, rangeStartTime]);

  const handleTimelineWindowMouseUp = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      event.stopPropagation();

      if (!rangeDragMovedRef.current) {
        void togglePlayback();
      }
    },
    [togglePlayback],
  );

  useEffect(() => {
    let cancelled = false;

    setIsGeneratingFrames(true);

    createTimelineFrames(videoSrc, timelineFrameCount)
      .then((nextFrames) => {
        if (!cancelled) {
          setFrames(nextFrames);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFrames([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsGeneratingFrames(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [timelineFrameCount, videoSrc]);

  const handleVideoTimeUpdate = useCallback(
    (event: SyntheticEvent<HTMLVideoElement>) => {
      const video = event.currentTarget;

      if (!duration) {
        setCurrentTime(video.currentTime);
        return;
      }

      if (
        !video.paused &&
        video.currentTime >= rangeEndTime - PLAYBACK_END_TOLERANCE
      ) {
        video.pause();
        video.currentTime = rangeEndTime;
        setCurrentTime(rangeEndTime);
        setIsPlaying(false);
        return;
      }

      if (video.currentTime < rangeStartTime) {
        video.currentTime = rangeStartTime;
        setCurrentTime(rangeStartTime);
        return;
      }

      setCurrentTime(video.currentTime);
    },
    [duration, rangeEndTime, rangeStartTime],
  );

  return (
    <section className="mx-auto flex h-[480px] w-full flex-col justify-center gap-4">
      <div className="overflow-hidden rounded-lg border border-white/10 bg-black shadow-2xl shadow-black/40">
        <video
          ref={videoRef}
          className="aspect-video h-full w-full bg-black object-contain"
          src={videoSrc}
          crossOrigin="anonymous"
          playsInline
          onLoadedMetadata={(event) => {
            const video = event.currentTarget;

            setDuration(video.duration);

            setVideoAspectRatio(getVideoAspectRatio(video));
          }}
          onTimeUpdate={handleVideoTimeUpdate}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onEnded={() => setIsPlaying(false)}
        />
      </div>

      <div className="w-full">
        <div className="relative w-full">
          <div
            aria-label="timeline-window"
            className={`absolute bottom-0 top-0 z-10 box-border rounded-md border-x-[24px] border-y-4 border-yellow-400 ${
              rangeDrag?.type === 'window' ? 'cursor-grabbing' : 'cursor-grab'
            }`}
            style={{
              left: `${rangeStartPx}px`,
              width: `${rangeEndPx - rangeStartPx + RANGE_HANDLE_WIDTH * 2}px`,
            }}
            title={isPlaying ? '点击暂停' : '点击播放'}
            onPointerDown={(event) => startRangeDrag('window', event)}
            onPointerUp={stopRangeDrag}
            onPointerCancel={stopRangeDrag}
            onMouseUp={handleTimelineWindowMouseUp}
            onClick={(event) => event.stopPropagation()}
          >
            <Tooltip title={formatTime(rangeStartTime)}>
              <div
                aria-label="left-window-handle"
                className={`pointer-events-auto absolute z-20 flex w-6 items-center justify-center transition-opacity ${
                  rangeDrag?.type === 'start'
                    ? 'cursor-grabbing'
                    : 'cursor-ew-resize'
                }`}
                style={{
                  bottom: '-4px',
                  left: `-${RANGE_HANDLE_WIDTH}px`,
                  top: '-4px',
                }}
                onPointerDown={(event) => startRangeDrag('start', event)}
                onPointerUp={stopRangeDrag}
                onPointerCancel={stopRangeDrag}
                onMouseUp={(event) => event.stopPropagation()}
                onClick={(event) => event.stopPropagation()}
              >
                <svg
                  width="24"
                  height="48"
                  viewBox="0 0 24 48"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M14.5 15L9.5 24L14.5 33"
                    stroke="#000"
                    stroke-width="4"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>
            </Tooltip>
            <Tooltip title={formatTime(rangeEndTime)}>
              <div
                aria-label="right-window-handle"
                className={`pointer-events-auto absolute z-20 flex w-6 items-center justify-center transition-opacity ${
                  rangeDrag?.type === 'end'
                    ? 'cursor-grabbing'
                    : 'cursor-ew-resize'
                }`}
                style={{
                  bottom: '-4px',
                  right: `-${RANGE_HANDLE_WIDTH}px`,
                  top: '-4px',
                }}
                onPointerDown={(event) => startRangeDrag('end', event)}
                onPointerUp={stopRangeDrag}
                onPointerCancel={stopRangeDrag}
                onMouseUp={(event) => event.stopPropagation()}
                onClick={(event) => event.stopPropagation()}
              >
                <svg
                  width="24"
                  height="48"
                  viewBox="0 0 24 48"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M9.5 15L14.5 24L9.5 33"
                    stroke="#000"
                    stroke-width="4"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>
            </Tooltip>
            <Tooltip title={formatTime(currentTime)}>
              <div
                className={`group absolute bottom-0 top-0 z-40 w-3 touch-none outline-none ring-offset-2 ring-offset-[#101115] focus-visible:ring-2 focus-visible:ring-sky-400 ${
                  isDragging ? 'cursor-grabbing' : 'cursor-[ew-resize]'
                }`}
                style={{
                  left: `${selectedProgress * 100}%`,
                  transform: 'translateX(-50%)',
                }}
                role="slider"
                tabIndex={0}
                aria-label="视频进度"
                aria-valuemin={0}
                aria-valuemax={Math.round(duration)}
                aria-valuenow={Math.round(currentTime)}
                title="拖动调整播放进度"
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={stopDragging}
                onPointerCancel={stopDragging}
                onMouseDown={handleMouseDown}
                onMouseUp={(event) => event.stopPropagation()}
                onClick={(event) => event.stopPropagation()}
                onKeyDown={(event) => {
                  if (!duration) {
                    return;
                  }

                  if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
                    event.preventDefault();
                    const direction = event.key === 'ArrowRight' ? 1 : -1;
                    const video = videoRef.current;
                    const nextTime = Math.min(
                      Math.max(currentTime + direction * 5, 0),
                      duration,
                    );

                    if (video) {
                      video.currentTime = nextTime;
                    }

                    setCurrentTime(nextTime);
                  }
                }}
              >
                <div
                  className="absolute inset-y-0 left-1/2 w-1 -translate-x-1/2 rounded-sm bg-white shadow-[0_0_8px_2px_rgba(0,0,0,0.4)]"
                  aria-hidden="true"
                />
              </div>
            </Tooltip>
          </div>
          <div className="relative flex h-20 flex-1 overflow-hidden rounded-md border-x-[24px] border-y-4 bg-gray-200">
            <div
              ref={timelineRef}
              className="relative h-full w-full min-w-0 overflow-hidden rounded bg-zinc-900 sm:flex-1"
            >
              <div className="flex h-full">
                {frames.length > 0
                  ? frames.map((frame) => (
                      <img
                        key={frame.time}
                        className="h-full flex-none object-fill"
                        style={{ width: `${timelineFrameWidth}px` }}
                        src={frame.src}
                        alt=""
                        draggable={false}
                      />
                    ))
                  : Array.from({ length: timelineFrameCount }).map(
                      (_, index) => (
                        <div
                          key={index}
                          className="h-full flex-none animate-pulse border-r border-white/5 bg-zinc-800"
                          style={{ width: `${timelineFrameWidth}px` }}
                        />
                      ),
                    )}
              </div>

              <div className="absolute inset-0 bg-gradient-to-r from-black/25 via-transparent to-black/25" />
              <div
                className="absolute inset-y-0 left-0 bg-black/45"
                style={{ width: `${playRange.start * 100}%` }}
                aria-hidden="true"
              />
              <div
                className="absolute inset-y-0 right-0 bg-black/45"
                style={{ width: `${(1 - playRange.end) * 100}%` }}
                aria-hidden="true"
              />
              <div
                className="absolute inset-y-0 bg-sky-400/20"
                style={{
                  left: `${playRange.start * 100}%`,
                  width: `${clamp(progress - playRange.start, 0, playRange.end - playRange.start) * 100}%`,
                }}
                aria-hidden="true"
              />

              {isGeneratingFrames && (
                <div className="absolute inset-0 grid place-items-center bg-black/35 text-xs font-medium text-zinc-200">
                  正在生成帧截图
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="mt-2 flex items-center justify-between px-6 text-xs tabular-nums text-zinc-400">
          <span>开始时间：{formatTime(rangeStartTime)}</span>
          <span>编辑时长：{formatTime(rangeEndTime - rangeStartTime)}</span>
          <span>结束时间：{formatTime(rangeEndTime)}</span>
        </div>
      </div>
    </section>
  );
}
